/**
 * 📦 COMPOSANT : sellerie-app/frontend/src\pages\Devis\ConfigurateurArticle\components\OptimisationCompact.tsx
 * Ce composant est utilisé dans d'autres pages de l'application
 */

/**
SELLERIE MARINE - COMPOSANT OPTIMISATION COMPACTE

📋 DESCRIPTION:
Composant React compact pour l'affichage et le contrôle de l'optimisation du calepinage.
Interface résumée avec actions principales et indicateurs d'économie.

🎯 RESPONSABILITÉS:
- Affichage du statut d'optimisation (actif/inactif)
- Indicateur d'économie réalisée avec montant
- Boutons d'action pour optimiser/désactiver/voir détail
- Interface compacte intégrée dans le configurateur principal
- Gestion des états visuels selon statut optimisation
- Transition fluide entre modes actif/inactif

🔗 DÉPENDANCES:
- React: Composant fonctionnel avec props
- CSS: Classes utilitaires pour interface compacte
- Parent: Callbacks pour actions optimisation

📡 INTERACTIONS:
- Parent: Réception état optimisation et données résultats
- Parent: Callbacks onOptimiser, onDesactiver, onVoirDetail
- Interface: Affichage conditionnel selon état optimisation
- Utilisateur: Actions rapides via boutons contextuels

📊 STATUT: ACTIF (Production)
📅 Dernière mise à jour: Juin 2025
👤 Responsable: Équipe développement

💡 NOTES:
- Interface compacte avec informations essentielles
- Affichage conditionnel économie si significative (>0.01€)
- Boutons contextuels selon état (optimiser vs désactiver/détail)
- Indicateurs visuels clairs (vert pour économie)
- Intégration harmonieuse dans layout principal
- Transitions CSS pour meilleure UX
*/
import React, { useState } from 'react';
import { PremiumInterestModal } from '../../../../components/PremiumInterestModal';

interface OptimisationCompactProps {
  optimisationActive: boolean;
  devisOptimise: any | null;
  hasPremiumFeatures: boolean;
  integrerEconomieCalepinage?: boolean;
  justInvalidated?: boolean; // ✅ NOUVEAU: Indicateur d'invalidation automatique
  onOptimiser: () => void;
  onDesactiver: () => void;
  onVoirDetail: () => void;
  onToggleIntegrerEconomie?: (value: boolean) => void;
}

const OptimisationCompact: React.FC<OptimisationCompactProps> = ({
  optimisationActive,
  devisOptimise,
  hasPremiumFeatures,
  integrerEconomieCalepinage = false,
  justInvalidated = false,
  onOptimiser,
  onDesactiver,
  onVoirDetail,
  onToggleIntegrerEconomie
}) => {
  const [showInterestModal, setShowInterestModal] = useState(false);

  return (
    <div className="bg-white shadow rounded-lg p-6 mt-6">
      {/* ✅ NOUVEAU: Alerte d'invalidation automatique */}
      {hasPremiumFeatures && !optimisationActive && justInvalidated && (
        <div className="mb-4 bg-amber-50 border border-amber-300 rounded-lg p-4 flex items-start space-x-3">
          <svg className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          <div className="flex-1">
            <h3 className="text-sm font-medium text-amber-800">
              ⚠️ Optimisation invalidée automatiquement
            </h3>
            <p className="text-sm text-amber-700 mt-1">
              Les articles du devis ont été modifiés. Le calepinage précédent n'est plus valide.
              <strong className="block mt-1">→ Relancez l'optimisation</strong> pour recalculer les économies possibles.
            </p>
          </div>
        </div>
      )}
      
      <div className="flex justify-between items-center">
        <div className="flex-1">
          <h2 className="text-lg font-medium">Optimisation du calepinage</h2>
          <div className="text-sm text-gray-600 mt-1">
            {hasPremiumFeatures ? (
              <>
                <span className="font-medium">
                  {optimisationActive ? 'Calepinage actif' : 'Calepinage inactif'}
                </span>
                {optimisationActive && devisOptimise && devisOptimise.economieTotal !== undefined && Math.abs(Number(devisOptimise.economieTotal)) > 0.01 && (
                  <>
                    <span className="mx-2">•</span>
                    <span className="text-green-600 font-medium">
                      Économie : −{Math.abs(devisOptimise.economieTotal).toFixed(2)} €
                    </span>
                  </>
                )}
              </>
            ) : (
              <>
                <span className="font-medium text-amber-600">⭐ Fonctionnalité Premium</span>
                <span className="mx-2">•</span>
                <span>Optimisez vos coûts matières jusqu'à 15%</span>
              </>
            )}
          </div>
          
          {/* Toggle pour intégrer l'économie au prix client */}
          {hasPremiumFeatures && optimisationActive && devisOptimise && Math.abs(Number(devisOptimise.economieTotal || 0)) > 0.01 && (
            <div className="mt-3 flex items-center">
              <label className="flex items-center cursor-pointer group">
                <div className="relative">
                  <input
                    type="checkbox"
                    className="sr-only"
                    checked={integrerEconomieCalepinage}
                    onChange={(e) => onToggleIntegrerEconomie?.(e.target.checked)}
                  />
                  <div className={`block w-10 h-6 rounded-full transition-colors ${
                    integrerEconomieCalepinage ? 'bg-blue-600' : 'bg-gray-300'
                  }`}></div>
                  <div className={`dot absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${
                    integrerEconomieCalepinage ? 'transform translate-x-4' : ''
                  }`}></div>
                </div>
                <span className="ml-3 text-sm font-medium text-gray-700">
                  Intégrer l'économie au prix client
                </span>
                <div className="ml-2 relative group/tooltip">
                  <svg className="w-4 h-4 text-gray-400 hover:text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                  </svg>
                  <div className="hidden group-hover/tooltip:block absolute z-10 w-64 px-3 py-2 text-xs text-white bg-gray-900 rounded-lg shadow-lg -top-2 left-6">
                    {integrerEconomieCalepinage 
                      ? "Le client bénéficie de l'optimisation (déduction appliquée)."
                      : "L'atelier conserve l'économie comme marge (aucune déduction)."}
                    <div className="absolute w-2 h-2 bg-gray-900 transform rotate-45 -left-1 top-3"></div>
                  </div>
                </div>
              </label>
            </div>
          )}
        </div>
        <div className="flex items-center space-x-3">
          {hasPremiumFeatures ? (
            optimisationActive && devisOptimise ? (
              <>
                <button
                  type="button"
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 font-medium transition-colors duration-200"
                  onClick={onDesactiver}
                >
                  Désactiver
                </button>
                <button
                  type="button"
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200"
                  onClick={onVoirDetail}
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2h2a2 2 0 002-2z" />
                  </svg>
                  Voir le détail
                </button>
              </>
            ) : (
              <button
                type="button"
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 font-medium transition-colors duration-200"
                onClick={onOptimiser}
              >
                Optimiser
              </button>
            )
          ) : (
            <button
              type="button"
              className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-md hover:from-amber-600 hover:to-orange-600 font-medium transition-all duration-200 shadow-md"
              onClick={() => setShowInterestModal(true)}
            >
              <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
              Découvrir le Premium
            </button>
          )}
        </div>
      </div>
      <PremiumInterestModal
        isOpen={showInterestModal}
        onClose={() => setShowInterestModal(false)}
        context="optimisation"
      />
    </div>
  );
};

export default OptimisationCompact;
